import { BeneficiaryDTO } from '@repo/shared/dtos/beneficiary'
import { CaseWorkerDTO } from '@repo/shared/dtos/caseWorker'
import { DocumentFileDTO, DocumentNoteDTO } from '@repo/shared/dtos/document'
import {
  OrganizationDTO,
  PromptKeywordDTO,
  PromptTemplateDTO,
  SudoContextOrganizationDTO,
} from '@repo/shared/dtos/organization'
import { ResourceDTO } from '@repo/shared/dtos/resources'
import { TaskDTO } from '@repo/shared/dtos/task'
import { SudoContextUserDTO, UserDTO } from '@repo/shared/dtos/user'
import { isNoteDocument } from '@repo/shared/utils'

import { PromptTemplate, Resource } from '@/store/admin'
import { Beneficiary } from '@/store/beneficiary'
import { OrganizationDemo } from '@/store/demo'
import { Document } from '@/store/document'
import { SudoContextOrganization, SudoContextUser } from '@/store/sudo'
import { Task } from '@/store/task'
import { CaseWorker, Organization, User } from '@/store/user'

// FIXME: rename it
export const buildBeneficiaryTaskFromDto = (v: TaskDTO): Task => ({
  archivedAt: v.archivedAt,
  beneficiaryId: v.beneficiaryId,
  cta: v.cta
    ? {
        link: v.cta.link,
        name: v.cta.name,
      }
    : undefined,
  categories: v.categories,
  content: v.content,
  createdAt: v.createdAt,
  dueDate: v.dueDate,
  durationInHours: v.durationInHours,
  id: v.id,
  title: v.title || '',
  updatedAt: v.updatedAt,
})

export const buildDocumentFromDto = (v: DocumentNoteDTO | DocumentFileDTO): Document => {
  if (isNoteDocument(v)) {
    return {
      content: v.content,
      createdAt: v.createdAt,
      id: v.id,
      type: v.type,
      updatedAt: v.updatedAt,
    }
  } else
    return {
      createdAt: v.createdAt,
      fileName: v.fileName,
      id: v.id,
      size: v.size,
      title: v.title,
      type: v.type,
      updatedAt: v.updatedAt,
    }
}

export const buildBeneficiaryFromDto = (v: BeneficiaryDTO): Beneficiary => ({
  caseWorkerIds: v.caseWorkerIds,
  createdAt: v.createdAt,
  fullName: v.fullName,
  id: v.id,
  profile: v.profile,
  status: v.status,
  structuredData: v.structuredData,
  updatedAt: v.updatedAt,
})

export const buildPromptTemplateFromDto = (
  v: PromptTemplateDTO,
  k: PromptKeywordDTO[],
): PromptTemplate => ({
  id: v.id,
  promptTemplateType: v.promptType,
  revision: v.revision,
  revisionDate: v.revisionDate,
  promptText: v.promptText,
  keywords: k.map((k) => ({
    apply: k.apply,
    isIterable: k.isIterable,
    key: k.key,
    section: k.section,
  })),
})

export const buildResourceFromDto = (v: ResourceDTO): Resource => ({
  content: v.content,
  createdAt: v.createdAt,
  id: v.id,
  title: v.title,
  updatedAt: v.updatedAt,
})

export const buildSudoContextUserFromDto = (v: SudoContextUserDTO): SudoContextUser => ({
  disabled: v.disabled,
  email: v.email,
  firstName: v.firstName,
  id: v.id,
  language: v.language,
  lastName: v.lastName,
  organizationIds: v.organizationIds,
  superAdmin: v.superAdmin,
})

export const buildUserFromDto = (data: UserDTO): User => {
  return {
    email: data.email,
    firstName: data.firstName,
    id: data.id,
    language: data.language,
    lastName: data.lastName,
    superAdmin: data.superAdmin,
  }
}

export const buildCaseWorkerFromDto = (data: CaseWorkerDTO): CaseWorker => {
  return {
    email: data.email,
    firstName: data.firstName,
    id: data.id,
    language: data.language,
    lastName: data.lastName,
    role: data.role,
    status: data.status,
  }
}

export const buildOrganizationFromDto = (v: OrganizationDTO): Organization => {
  return {
    lifeCycleStage: v.lifeCycleStage,
    chatSettings: v.chatSettings,
    description: v.description,
    featureFlags: v.featureFlags,
    id: v.id,
    language: v.language,
    methodology: v.methodology,
    name: v.name,
    onboarding: v.onboarding,
    texts: v.texts ?? undefined,
    // because v.url can be null
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    url: v.url === null ? undefined : v.url,
    chatAssistantName: v.chatAssistantName,
    chatAnswerGoal: v.chatAnswerGoal,
    chatMessageInstructions: v.chatMessageInstructions,
    chatSafetyRules: v.chatSafetyRules,
    categoriesSettings: v.categoriesSettings || [],
    commonSources: v.commonSources,
    structuredDataSchema: v.structuredDataSchema,
    structuredDataDisplay: v.structuredDataDisplay,
    beneficiaryProfileTemplate:
      v.beneficiaryProfileTemplate === null ? undefined : v.beneficiaryProfileTemplate,
  }
}

export const buildSudoContextOrganizationFromDto = (
  v: SudoContextOrganizationDTO,
): SudoContextOrganization => {
  return {
    lifeCycleStage: v.lifeCycleStage,
    id: v.id,
    name: v.name,
    url: v.url,
    featureFlags: v.featureFlags,
  }
}

export const buildOrganizationDemoFromDto = (v: SudoContextOrganizationDTO): OrganizationDemo => {
  return {
    id: v.id,
    name: v.name,
    url: v.url,
    featureFlags: v.featureFlags,
    lifeCycleStage: v.lifeCycleStage,
  }
}
